import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import Footer from '../../components/Footer';
import { 
  Headphones, 
  MessageCircle, 
  Mail, 
  Phone,
  ChevronDown,
  ChevronUp,
  Search,
  AlertTriangle,
  Shield
} from 'lucide-react';

const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="border-b border-slate-200 last:border-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-4 flex items-center justify-between text-left"
      >
        <span className="font-medium text-slate-800">{question}</span>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-slate-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400" />
        )}
      </button>
      {isOpen && (
        <div className="pb-4 text-slate-600">
          {answer}
        </div>
      )}
    </div>
  );
};

export default function SupportPage() {
  const faqs = [
    {
      question: "¿Cómo solicito un viaje?",
      answer: "Abre la app, selecciona tu punto de recogida y destino, elige el tipo de vehículo y confirma tu solicitud. Un conductor cercano aceptará tu viaje en segundos."
    },
    {
      question: "¿Cómo me registro como conductor?",
      answer: "Ve a la sección 'Conductores' y completa el formulario de registro. Necesitarás licencia de conducir vigente, seguro de auto y pasar la verificación de antecedentes."
    },
    {
      question: "¿Cómo funciona el pago con DIGGII?",
      answer: "DIGGII es nuestro token utilitario con valor 1:1 USD. Puedes usarlo para pagar servicios, recibir bonos por referidos y obtener cashback en tus viajes."
    },
    {
      question: "¿Qué hago si tengo un problema durante un viaje?",
      answer: "Usa el botón de emergencia dentro de la app para contactar soporte inmediato. También puedes reportar el problema después del viaje desde tu historial."
    },
    {
      question: "¿Cómo rento mi vehículo?",
      answer: "Regístrate como dueño de vehículo, sube fotos y detalles de tu auto, establece tus precios y disponibilidad. Los usuarios verificados podrán enviarte solicitudes de renta."
    },
    {
      question: "¿Cómo retiro mis ganancias?",
      answer: "Tus ganancias se acumulan en tu Digi Wallet. Desde ahí puedes transferir a tu cuenta bancaria o usar el saldo para otros servicios de Mercapoli."
    }
  ];

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_mercamove/artifacts/ch8knfdf_Move_Logo_wHT.png" 
                alt="Mercapoli Move"
                className="h-8"
              />
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" className="text-slate-700 hover:text-[#00BFA6]">
                  Iniciar Sesión
                </Button>
              </Link>
              <Link to="/register">
                <Button className="btn-primary px-6 py-2 text-sm">
                  Registrarse
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 bg-gradient-to-br from-[#00BFA6]/5 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-20 h-20 bg-[#00BFA6]/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Headphones className="w-10 h-10 text-[#00BFA6]" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 font-['Outfit'] leading-tight">
            Centro de <span className="text-[#00BFA6]">Soporte</span>
          </h1>
          <p className="text-lg text-slate-600 mt-6 max-w-2xl mx-auto">
            Estamos aquí para ayudarte. Accede a soporte técnico, preguntas frecuentes y 
            canales de contacto para resolver cualquier duda.
          </p>
        </div>
      </section>

      {/* Quick Actions */}
      <section className="py-12 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-6">
            <div className="bg-slate-50 p-6 rounded-2xl text-center hover:shadow-lg transition-shadow cursor-pointer">
              <MessageCircle className="w-10 h-10 text-[#00BFA6] mx-auto mb-3" />
              <h3 className="font-bold text-slate-800">Chat en vivo</h3>
              <p className="text-slate-500 text-sm mt-1">Respuesta inmediata</p>
            </div>
            <div className="bg-slate-50 p-6 rounded-2xl text-center hover:shadow-lg transition-shadow cursor-pointer">
              <Mail className="w-10 h-10 text-[#FFD166] mx-auto mb-3" />
              <h3 className="font-bold text-slate-800">Email</h3>
              <p className="text-slate-500 text-sm mt-1">soporte@mercapolimove.com</p>
            </div>
            <div className="bg-slate-50 p-6 rounded-2xl text-center hover:shadow-lg transition-shadow cursor-pointer">
              <Phone className="w-10 h-10 text-[#00887A] mx-auto mb-3" />
              <h3 className="font-bold text-slate-800">Teléfono</h3>
              <p className="text-slate-500 text-sm mt-1">+52 800 123 4567</p>
            </div>
            <div className="bg-slate-50 p-6 rounded-2xl text-center hover:shadow-lg transition-shadow cursor-pointer">
              <AlertTriangle className="w-10 h-10 text-red-500 mx-auto mb-3" />
              <h3 className="font-bold text-slate-800">Emergencias</h3>
              <p className="text-slate-500 text-sm mt-1">24/7 disponible</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4 bg-[#F5F7FA]">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 font-['Outfit'] text-center mb-8">
            Preguntas Frecuentes
          </h2>
          <div className="bg-white rounded-2xl shadow-lg p-6">
            {faqs.map((faq, index) => (
              <FAQItem key={index} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 font-['Outfit'] text-center mb-8">
            ¿No encontraste lo que buscabas?
          </h2>
          <div className="bg-slate-50 rounded-2xl p-8">
            <form className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-slate-700 text-sm font-medium mb-2">Nombre</label>
                  <Input 
                    placeholder="Tu nombre" 
                    className="h-12 bg-white border-slate-200 rounded-xl"
                  />
                </div>
                <div>
                  <label className="block text-slate-700 text-sm font-medium mb-2">Email</label>
                  <Input 
                    type="email"
                    placeholder="tu@email.com" 
                    className="h-12 bg-white border-slate-200 rounded-xl"
                  />
                </div>
              </div>
              <div>
                <label className="block text-slate-700 text-sm font-medium mb-2">Asunto</label>
                <Input 
                  placeholder="¿En qué podemos ayudarte?" 
                  className="h-12 bg-white border-slate-200 rounded-xl"
                />
              </div>
              <div>
                <label className="block text-slate-700 text-sm font-medium mb-2">Mensaje</label>
                <Textarea 
                  placeholder="Describe tu problema o pregunta..."
                  className="bg-white border-slate-200 rounded-xl resize-none h-32"
                />
              </div>
              <Button className="btn-primary w-full h-12">
                Enviar mensaje
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section className="py-16 px-4 bg-[#00BFA6]/5">
        <div className="max-w-4xl mx-auto text-center">
          <Shield className="w-16 h-16 text-[#00BFA6] mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 font-['Outfit'] mb-4">
            Tu seguridad es nuestra prioridad
          </h2>
          <p className="text-slate-600">
            Nunca te pediremos contraseñas, códigos de verificación o información financiera 
            por teléfono o email. Si recibes comunicaciones sospechosas, repórtalas inmediatamente.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
